import {
  Table, TableHead, TableBody, TableRow, TableCell, Chip, Box, Typography,
} from "@mui/material";
import type { BinderVerificationSummaryRow } from "@taxassist/api-client";

interface Props {
  rows: BinderVerificationSummaryRow[];
  onRowClick: (jobId: string) => void;
}

/**
 * `verification.status` reflects what's literally in the binder PDF
 * (verified/issues_found/unverifiable) and is set by the verifier service.
 *
 * `reviewer_complete` is a separate workflow state: every missing-doc row
 * has carried a `reviewerVerdict` (the reviewer either confirmed it's
 * present anyway or marked it as not required). The condition mirrors the
 * server-side `reviewer_complete` filter at jobService.ts:55-60.
 */
function isReviewerResolved(row: BinderVerificationSummaryRow): boolean {
  return (
    row.status === "issues_found" &&
    row.missingCount === 0 &&
    row.resolvedCount > 0
  );
}

function StatusBadge({ row }: { row: BinderVerificationSummaryRow }) {
  if (row.pipelineStatus === "pending" || row.pipelineStatus === "processing") {
    return <Chip size="small" color="info" label="Processing" />;
  }
  if (row.pipelineStatus === "failed") {
    return <Chip size="small" color="error" label="Failed" />;
  }
  if (!row.status) return <Chip size="small" label="Not verified" />;
  if (row.status === "verified") return <Chip size="small" color="success" label="Verified" />;
  if (row.status === "issues_found") {
    return isReviewerResolved(row) ? (
      <Chip size="small" color="success" variant="outlined" label="Resolved" />
    ) : (
      <Chip size="small" color="warning" label="Issues" />
    );
  }
  if (row.status === "unverifiable") return <Chip size="small" label="Unverifiable" />;
  return null;
}

export function BinderVerificationTable({ rows, onRowClick }: Props) {
  if (rows.length === 0) {
    return (
      <Box p={4} textAlign="center">
        <Typography color="text.secondary">No binders match this filter.</Typography>
      </Box>
    );
  }
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Client</TableCell>
          <TableCell>Binder uploaded</TableCell>
          <TableCell>Status</TableCell>
          <TableCell align="right">Missing (unresolved)</TableCell>
          <TableCell align="right">Missing (resolved)</TableCell>
          <TableCell align="right">Pages</TableCell>
          <TableCell>Last verified</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r) => (
          <TableRow
            key={r.jobId}
            hover
            sx={{ cursor: "pointer" }}
            onClick={() => onRowClick(r.jobId)}
          >
            <TableCell>{r.clientName}</TableCell>
            <TableCell>{new Date(r.binderUploadedAt).toLocaleDateString()}</TableCell>
            <TableCell><StatusBadge row={r} /></TableCell>
            <TableCell align="right">{r.missingCount}</TableCell>
            <TableCell align="right">{r.resolvedCount}</TableCell>
            <TableCell align="right">
              {r.totalPages ?? "—"}
              {r.partiallyScanned && <Chip size="small" sx={{ ml: 1 }} label="Scanned" />}
            </TableCell>
            <TableCell>
              {r.lastVerifiedAt ? new Date(r.lastVerifiedAt).toLocaleString() : "—"}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
